/**
 * Frontend error reporter adapter — public surface app code uses
 * (#1241 Swappability rails).
 *
 * Mirrors the backend's `infrastructure/errorReporter/` shape but for
 * the browser surface. Application components, hooks, and route
 * handlers import `getErrorReporter()` from here and call the
 * `ErrorReporter` methods. They MUST NOT import from `@sentry/*`
 * directly — the acceptance check is `git grep "@sentry/"
 * apps/frontend/src` returning matches ONLY inside this directory.
 *
 * Why this layer exists
 * ─────────────────────
 * The primary integration (#1241) wires `@sentry/react` pointed at
 * Sentry SaaS. The operator may later want to revert (one env var
 * flip) or swap providers (one env var change) without code edits.
 * See the server adapter's header for the full rationale.
 *
 * Master toggle
 * ─────────────
 * `VITE_ERROR_REPORTER_ENABLED=true|false`. Vite-prefixed so it bakes
 * into the bundle at build time. When `false` (the default), the
 * adapter returns a no-op — `captureException` / `captureMessage`
 * still log via `console.warn` so devs see errors locally.
 *
 * Provider parameter
 * ──────────────────
 * `VITE_ERROR_REPORTER_PROVIDER=sentry-saas|none`. Default `none`.
 * `sentry-saas` routes through `@sentry/react` pointed at the
 * operator's Sentry SaaS DSN.
 *
 * Revert path
 * ───────────
 * Set `VITE_ERROR_REPORTER_ENABLED=false` at build time and ship the
 * new bundle. All capture stops; no code changes.
 */

/** Context attached to a single capture call. */
export interface ErrorContext {
  tags?: Record<string, string>
  extra?: Record<string, unknown>
  level?: 'info' | 'warn' | 'error' | 'fatal'
}

/**
 * Vendor-neutral error reporter surface. Same shape as the backend
 * so a contributor moving between surfaces uses the same idiom.
 */
export interface ErrorReporter {
  captureException(err: unknown, context?: ErrorContext): void
  captureMessage(
    message: string,
    level?: 'info' | 'warn' | 'error',
    context?: ErrorContext,
  ): void
  setContext(name: string, ctx: Record<string, unknown>): void
  addBreadcrumb(crumb: {
    category: string
    message: string
    level?: string
    data?: Record<string, unknown>
  }): void
  setUser(user: { id?: string; workspaceId?: string } | null): void
  setTag(key: string, value: string): void
  flush(timeout?: number): Promise<boolean>
}

interface ReporterConfig {
  enabled: boolean
  provider: 'sentry-saas' | 'none'
}

/**
 * Read the build-time env config. Vite replaces `import.meta.env.*`
 * at build, so these are constants in the shipped bundle. The
 * defaults are the safe-off shape: master toggle false, provider
 * none — a build that omits the env vars ships with capture OFF.
 */
function readConfig(): ReporterConfig {
  // `import.meta.env.VITE_*` is typed as `any` by Vite's default
  // shim; coerce + trim defensively.
  const enabledRaw = String(import.meta.env.VITE_ERROR_REPORTER_ENABLED ?? '')
    .trim()
    .toLowerCase()
  const enabled = enabledRaw === 'true'
  const providerRaw = String(import.meta.env.VITE_ERROR_REPORTER_PROVIDER ?? '')
    .trim()
    .toLowerCase()
  let provider: ReporterConfig['provider'] = 'none'
  if (providerRaw === 'sentry-saas') provider = 'sentry-saas'
  return { enabled, provider }
}

/**
 * No-op reporter. Returned when capture is disabled. `captureException`
 * / `captureMessage` log via `console.warn` so a dev sees the error
 * locally even when remote capture is off.
 */
function createNoopReporter(): ErrorReporter {
  return {
    captureException(err, ctx) {
      const message = err instanceof Error ? (err.stack ?? err.message) : String(err)
      console.warn('[errorReporter:noop] captureException', message, ctx)
    },
    captureMessage(message, level, ctx) {
      console.warn(`[errorReporter:noop] captureMessage [${level ?? 'info'}]`, message, ctx)
    },
    setContext() {
      // No-op.
    },
    addBreadcrumb() {
      // No-op.
    },
    setUser() {
      // No-op.
    },
    setTag() {
      // No-op.
    },
    async flush() {
      return true
    },
  }
}

/**
 * Build a reporter targeting the existing `@sentry/react` integration
 * (`./sentryReporter.ts`). Dynamic import keeps `@sentry/react` out of
 * the bundle when the adapter is in no-op mode at build time —
 * Vite's tree-shaker can drop the entire branch.
 *
 * Caveat: dynamic import in the browser doesn't tree-shake as
 * aggressively as a static unreachable branch would. Vite still emits
 * a separate chunk for `./sentryReporter.ts` even when the adapter is
 * configured off. The chunk is small (the SDK is in its own chunk)
 * and only fetched if the reporter is actually instantiated, so the
 * cost of an off-by-default deploy is one tiny additional chunk on
 * disk — not on the wire.
 */
function createSentryReporter(): ErrorReporter {
  let impl: typeof import('./sentryReporter.js') | null = null
  let initPromise: Promise<void> | null = null

  const ensureLoaded = async (): Promise<void> => {
    if (impl) return
    if (!initPromise) {
      initPromise = import('./sentryReporter.js')
        .then((m) => {
          impl = m
          m.initSentryReporter()
        })
        .catch((err) => {
          console.warn(
            `[errorReporter] Sentry SDK init failed; reverting to no-op: ${
              err instanceof Error ? err.message : String(err)
            }`,
          )
          impl = null
        })
    }
    await initPromise
  }

  void ensureLoaded()

  return {
    captureException(err, ctx) {
      if (!impl) {
        console.warn(
          '[errorReporter] capture before SDK ready; logging only',
          err instanceof Error ? (err.stack ?? err.message) : String(err),
          ctx,
        )
        return
      }
      impl.sentryCaptureException(err, ctx)
    },
    captureMessage(message, level, ctx) {
      if (!impl) {
        console.warn(`[errorReporter] message before SDK ready [${level ?? 'info'}]`, message, ctx)
        return
      }
      impl.sentryCaptureMessage(message, level, ctx)
    },
    setContext(name, ctx) {
      if (!impl) return
      impl.sentrySetContext(name, ctx)
    },
    addBreadcrumb(crumb) {
      if (!impl) return
      impl.sentryAddBreadcrumb(crumb)
    },
    setUser(user) {
      if (!impl) return
      impl.sentrySetUser(user)
    },
    setTag(key, value) {
      if (!impl) return
      impl.sentrySetTag(key, value)
    },
    async flush(timeout) {
      if (!impl) return true
      return impl.sentryFlush(timeout)
    },
  }
}

function createReporter(config: ReporterConfig): ErrorReporter {
  if (!config.enabled) return createNoopReporter()
  switch (config.provider) {
    case 'sentry-saas':
      return createSentryReporter()
    case 'none':
      return createNoopReporter()
  }
}

let _reporter: ErrorReporter | null = null

/** Lazy singleton. App code calls `getErrorReporter()` at every
 * capture site. */
export function getErrorReporter(): ErrorReporter {
  if (!_reporter) {
    _reporter = createReporter(readConfig())
  }
  return _reporter
}

/**
 * Explicit boot-time init. Optional — the lazy singleton path also
 * works — but calling it from `main.tsx` makes the provider-decision
 * visible in the console at boot.
 *
 * Safe to call multiple times; subsequent calls are no-ops.
 */
export function initErrorReporter(): void {
  if (_reporter) return
  const config = readConfig()
  _reporter = createReporter(config)
  console.log(
    `[errorReporter] initialized — enabled=${config.enabled} provider=${config.provider}`,
  )
}

/** Reset the singleton for tests. */
export function __resetReporterForTesting(): void {
  _reporter = null
}

/**
 * React error boundary re-export. Lazy-resolved via dynamic import so
 * the boundary component itself doesn't import `@sentry/react` at the
 * module-graph top level. App code imports `ErrorBoundary` from here.
 *
 * The boundary's behavior degrades gracefully when capture is off:
 * the fallback UI still renders on a thrown child error, but no
 * event is shipped. See the implementation file's comment for the
 * exact behavior matrix.
 */
export { ErrorBoundary } from './errorBoundary.js'
